import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Button, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import useShipments from "../../hooks/useShipments";
import ShipmentForm from "../../components/shipments/ShipmentForm";
import ShipmentCreatedDialog from "../../components/shipments/ShipmentCreatedDialog";

// The admin single-shipment create form, as its own page
// (/admin/shipments/add) instead of a modal — same ShipmentForm
// component, same fields, same validation, same create logic as before
// (see AdminShipments.jsx's now-removed inline create dialog, which this
// mirrors exactly, including the admin-only customer picker via
// `isAdmin`). ShipmentForm itself no longer has a Dialog-mode at all —
// every caller renders it inline like this now. Mirrors
// CustomerAddShipment.jsx's same page-ification, same styling.
const AdminAddShipment = () => {
  const navigate = useNavigate();
  const { create } = useShipments();

  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  // Bumped on "Create Another" to force ShipmentForm to fully
  // remount — the simplest way to get a genuinely blank form again
  // without touching that component's own internal reset logic.
  const [formKey, setFormKey] = useState(0);

  const handleCreate = async (data, filesMeta) => {
    setCreating(true);
    try {
      const result = await create(data);
      if (result) {
        if (filesMeta?.pendingFiles?.length > 0) {
          try {
            const shipment = result;
            let itemId;
            if (filesMeta.attachTo && filesMeta.attachTo.startsWith("item:")) {
              const idx = parseInt(filesMeta.attachTo.split(":")[1], 10);
              itemId = shipment.items?.[idx]?._id;
            }
            await import("../../services/documentService").then((mod) =>
              mod.default.upload(shipment._id, filesMeta.pendingFiles, filesMeta.docType, undefined, itemId),
            );
          } catch (err) {
            // ignore upload errors here; admin can upload later from the shipment detail page
          }
        }
        setJustCreated(result);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/admin/shipments"))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            New Shipment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Log a new shipment on a customer's behalf
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
        <ShipmentForm
          key={formKey}
          open
          onClose={() => navigate("/admin/shipments")}
          onSubmit={handleCreate}
          loading={creating}
          isAdmin
        />
      </Card>

      <ShipmentCreatedDialog
        open={!!justCreated}
        trackingNumber={justCreated?.trackingNumber}
        onClose={() => navigate("/admin/shipments")}
        onCreateAnother={() => {
          setJustCreated(null);
          setFormKey((k) => k + 1);
        }}
      />
    </Box>
  );
};

export default AdminAddShipment;
